import { useMemo, useState } from 'react';
import { Autocomplete, Box, Popover, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { CasChip } from 'src/compass/shared/components';
import { CasTagChip, ICasTag } from 'src/compass/application';
import { useApiContext } from 'src/compass/site/providers';
import { useRetrieveSelectListOptions } from 'src/compass/shared/components/CasSelect/CasEntitySelect';

interface IProps {
  entityId: number;
  tags: ICasTag[];
  // Fires after each assign/unassign settles (success or failure) so the
  // parent dialog can flag dataUpdated and refresh the customer listing on close.
  onChanged?: () => void;
}

const TAG_ENTITY = 'Tag';
const ACTIVE_PARAMS = { isActive: true };

// Mirror useAPI's queryKey shape (`[url, { data, params }]`) so optimistic
// setQueryData lands in the same cache slot that the dialog's
// useRetrieveDetailsById('Utilities/EntityTouchpoint', id) reads from.
const detailCacheKey = (entityId: number) => [
  `Utilities/EntityTouchpoint/${entityId}`,
  { data: {}, params: undefined },
];

export default function TagChips({ entityId, tags, onChanged }: IProps) {
  const queryClient = useQueryClient();
  const { api } = useApiContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Controlled open for the Autocomplete listbox. We defer opening until the
  // Popover's enter transition has finished so Popper can measure the input's
  // final position — if the listbox opens in the first frame it can render on
  // top of the input.
  const [listboxOpen, setListboxOpen] = useState(false);

  const { options: allTagOptions, isLoading: optionsLoading } =
    useRetrieveSelectListOptions(TAG_ENTITY, { params: ACTIVE_PARAMS });

  // Exclude tags already on this entity so the picker only offers additions.
  const pickableTags = useMemo<ICasTag[]>(() => {
    const assigned = new Set(tags.map((t) => t.id));
    return (allTagOptions as ICasTag[]).filter((t) => !assigned.has(t.id));
  }, [allTagOptions, tags]);

  const cacheKey = detailCacheKey(entityId);

  const optimisticallyUpdateTags = (
    transform: (current: ICasTag[]) => ICasTag[]
  ): { previous: any } => {
    const previous = queryClient.getQueryData<any>(cacheKey);
    queryClient.setQueryData(cacheKey, (old: any) => ({
      ...old,
      tags: transform(old?.tags ?? []),
    }));
    return { previous };
  };

  const assign = useMutation({
    mutationFn: (tag: ICasTag) => api.post(`Entity/Tag/${entityId}/${tag.id}`),
    onMutate: async (tag) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      return optimisticallyUpdateTags((curr) => [...curr, tag]);
    },
    onError: (_err, _tag, ctx) => {
      if (ctx) queryClient.setQueryData(cacheKey, ctx.previous);
      enqueueSnackbar("Couldn't add tag", { variant: 'error' });
    },
    onSettled: () => {
      // Intentionally NOT invalidating the dialog's detail query — a refetch
      // would flip isRefetching=true and surface CasLoaderWithBackdrop, which
      // blocks the UI. The optimistic setQueryData above (and the onError
      // rollback) keep the dialog's tags accurate without a network round
      // trip. The parent customer listing refresh is deferred until the
      // dialog closes via onChanged.
      onChanged?.();
    },
  });

  const unassign = useMutation({
    mutationFn: (tagId: number) => api.delete(`Entity/Tag/${entityId}/${tagId}`),
    onMutate: async (tagId) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      return optimisticallyUpdateTags((curr) => curr.filter((t) => t.id !== tagId));
    },
    onError: (_err, _tagId, ctx) => {
      if (ctx) queryClient.setQueryData(cacheKey, ctx.previous);
      enqueueSnackbar("Couldn't remove tag", { variant: 'error' });
    },
    onSettled: () => {
      // Intentionally NOT invalidating the dialog's detail query — a refetch
      // would flip isRefetching=true and surface CasLoaderWithBackdrop, which
      // blocks the UI. The optimistic setQueryData above (and the onError
      // rollback) keep the dialog's tags accurate without a network round
      // trip. The parent customer listing refresh is deferred until the
      // dialog closes via onChanged.
      onChanged?.();
    },
  });

  const openPicker = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const closePicker = () => {
    setAnchorEl(null);
    setListboxOpen(false);
  };

  const handlePick = (_event: unknown, value: ICasTag | null) => {
    if (value) assign.mutate(value);
    closePicker();
  };

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
      {tags.map((tag) => (
        <CasTagChip key={tag.id} tag={tag} onDelete={() => unassign.mutate(tag.id)} />
      ))}
      <Box component="span" onClick={openPicker} sx={{ cursor: 'pointer' }}>
        <CasChip label="+ Add tag" size="small" color="disabled" onClick={openPicker} />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{
          // Open the listbox once the Popover has fully animated in. This
          // guarantees Popper measures the input at its final position rather
          // than at the initial mid-transition coordinates (which is what made
          // the listbox cover the input previously).
          onEntered: () => setListboxOpen(true),
        }}
      >
        <Box sx={{ p: 1, width: 260 }}>
          <Autocomplete<ICasTag>
            open={listboxOpen}
            onOpen={() => setListboxOpen(true)}
            onClose={() => setListboxOpen(false)}
            options={pickableTags}
            loading={optionsLoading}
            getOptionLabel={(option) => option.name}
            // Each tag in its own color, as it will look once added.
            renderOption={(rProps, option) => (
              <li {...rProps} key={option.id}>
                <CasTagChip tag={option} />
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handlePick}
            autoHighlight
            noOptionsText={optionsLoading ? 'Loading…' : 'No tags available'}
            // 4px offset is a belt-and-suspenders guard on top of the deferred
            // open — if Popper ever races ahead, the listbox still won't
            // visually overlap the search input.
            componentsProps={{
              popper: {
                modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
              },
            }}
            renderInput={(params) => (
              <TextField {...params} autoFocus size="small" placeholder="Search tags…" />
            )}
          />
        </Box>
      </Popover>
    </Stack>
  );
}
